"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GmClientService = void 0;
const web3_js_1 = require("@solana/web3.js");
const instruction_builders_1 = require("../instruction_builders");
const pda_getters_1 = require("../pda_getters");
const helpers_1 = require("./helpers");
const util_1 = require("../../util");
const Order_1 = require("../models/Order");
const constants_1 = require("./constants");
const utils_1 = require("../utils");
const atlas_staking_1 = require("../../atlas-staking");
/**
 * Provides utility methods and transaction builders for interacting with the Galactic Marketplace.
 */
class GmClientService {
    constructor() {
        this.currencyInfo = [];
    }
    /**
     * Returns information about all valid quote currencies registered with the Galactic Marketplace
     *
     * @param connection Solana Connection
     * @param programId Marketplace program PublicKey
     * @param invalidateCache Forces currency cache to be invalidated
     */
    getRegisteredCurrencies(connection, programId, invalidateCache = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (((_a = this.currencyInfo) === null || _a === void 0 ? void 0 : _a.length) && !invalidateCache)
                return this.currencyInfo;
            const result = [];
            const currencyInfo = yield (0, pda_getters_1.getAllRegisteredCurrencies)(connection, programId);
            for (const info of currencyInfo) {
                const { mint, royalty, saVault } = info;
                const tokenSupplylInformation = yield connection.getTokenSupply(mint, 'recent');
                const { decimals } = tokenSupplylInformation.value;
                result.push({
                    decimals,
                    mint: mint.toString(),
                    royaltyPercentageAsDecimal: royalty.toNumber() / constants_1.ONE_MILLION,
                    saVault: saVault.toString(),
                });
            }
            this.currencyInfo = result;
            return this.currencyInfo;
        });
    }
    /**
     * Fetches all open orders from the Galactic Marketplace.
     *
     * @param connection Solana Connection
     * @param programId Marketplace program PublicKey
     */
    getAllOpenOrders(connection, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getAllOpenOrders)(connection, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetches all open orders for a player PublicKey.
     *
     * @param connection Solana Connection
     * @param playerPublicKey User PublicKey
     * @param programId Marketplace program PublicKey
     */
    getOpenOrdersForPlayer(connection, playerPublicKey, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getOpenOrdersForPlayer)(connection, playerPublicKey, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetches all open orders from the Galactic Marketplace filtered by currency.
     *
     * @param connection Solana Connection
     * @param currencyMint Currency mint - use `getAllGmRegisteredCurrencyInfo` for a list of valid currencies
     * @param programId Marketplace program PublicKey
     * @returns
     */
    getOpenOrdersForCurrency(connection, currencyMint, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getOpenOrdersForCurrency)(connection, currencyMint, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetches all open orders from the Galactic Marketplace filtered by asset.
     *
     * @param connection Solana Connection
     * @param assetMint The token PublicKey
     * @param programId Marketplace program PublicKey
     * @returns
     */
    getOpenOrdersForAsset(connection, assetMint, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getOpenOrdersForAsset)(connection, assetMint, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetches all open orders from the Galactic Marketplace filtered by asset.
     *
     * @param connection Solana Connection
     * @param playerPublicKey User PublicKey
     * @param currencyMint Currency mint - use `getAllGmRegisteredCurrencyInfo` for a list of valid currencies
     * @param programId Marketplace program PublicKey
     * @returns
     */
    getOpenOrdersForPlayerAndCurrency(connection, playerPublicKey, currencyMint, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getOpenOrdersForPlayerAndCurrency)(connection, playerPublicKey, currencyMint, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetches all open orders from the Galactic Marketplace filtered by asset.
     *
     * @param connection Solana Connection
     * @param playerPublicKey User PublicKey
     * @param assetMint The token PublicKey
     * @param programId Marketplace program PublicKey
     * @returns
     */
    getOpenOrdersForPlayerAndAsset(connection, playerPublicKey, assetMint, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const openOrders = yield (0, pda_getters_1.getOpenOrdersForPlayerAndAsset)(connection, playerPublicKey, assetMint, programId);
            const slotContext = yield connection.getSlot();
            return this.accountItemsToOrders(connection, programId, openOrders, slotContext);
        });
    }
    /**
     * Fetch a single open order
     *
     * @param connection Solana Connection
     * @param orderAccount The `order.id` as PublicKey
     * @param programId Marketplace program PublicKey
     * @returns
     */
    getOpenOrder(connection, orderAccount, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const orderAccountInfo = yield (0, pda_getters_1.getSingleOrder)(connection, orderAccount, programId);
            const orderAccountItem = {
                publicKey: orderAccount,
                account: orderAccountInfo,
            };
            const slotContext = yield connection.getSlot();
            const [result] = yield this.accountItemsToOrders(connection, programId, [orderAccountItem], slotContext);
            return result;
        });
    }
    /**
     *
     * Get a Transaction to initialize either a buy or sell order
     *
     * @param connection Solana Connection
     * @param orderCreator Order creator
     * @param itemMint Item mint, e.g. Discovery of Iris
     * @param quoteMint The quote currency, e.g. ATLAS, USDC
     * @param quantity Self-explanatory
     * @param price The price of the item
     * @param programId Galactic Marketplace program ID
     * @param orderSide The order side from the perspective of the order creator
     */
    getInitializeOrderTransaction(connection, orderCreator, itemMint, quoteMint, quantity, price, programId, orderSide) {
        return __awaiter(this, void 0, void 0, function* () {
            /** Default to sell order values */
            let orderMethod = instruction_builders_1.createInitializeSellOrderInstruction;
            let depositMint = itemMint;
            let receiveMint = quoteMint;
            /** Overwrite with correct values if orderCreator is buying */
            if (orderSide === Order_1.OrderSide.Buy) {
                orderMethod = instruction_builders_1.createInitializeBuyOrderInstruction;
                depositMint = quoteMint;
                receiveMint = itemMint;
            }
            const initializerDepositTokenAccount = yield (0, util_1.getAssociatedTokenAddress)(orderCreator, depositMint);
            const { ixSet } = yield orderMethod({
                connection,
                depositMint,
                initializerDepositTokenAccount,
                initializerMainAccount: orderCreator,
                originationQty: quantity,
                price,
                programId,
                receiveMint,
            });
            const { signers, instructions } = ixSet;
            const transaction = (0, helpers_1.createTransactionFromInstructions)(instructions);
            return { transaction, signers };
        });
    }
    getBnPriceForCurrency(connection, uiPrice, quoteCurrency, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const allCurrencyInfo = yield this.getRegisteredCurrencies(connection, programId);
            const { decimals } = allCurrencyInfo.find((info) => info.mint.toString() === quoteCurrency.toString());
            return (0, utils_1.convertDecimalPriceToBn)(uiPrice, decimals);
        });
    }
    /**
     *
     * @param connection Solana Connection
     * @param signer The market authority or the order initializer
     * @param orderAccount The order account PublicKey (order ID)
     * @param orderInitializer The PublicKey which created the order
     * @param programId Galactic Marketplace program ID
     */
    getCancelOrderTransaction(connection, orderAccount, orderInitializer, programId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { instructions, signers } = yield (0, instruction_builders_1.createCancelOrderInstruction)({
                connection,
                orderAccount,
                orderInitializer,
                programId,
            });
            const transaction = (0, helpers_1.createTransactionFromInstructions)(instructions);
            return { transaction, signers };
        });
    }
    /**
     *
     * @param connection Solana Connection
     * @param order The order account PublicKey (order ID)
     * @param orderTaker The PublicKey purchasing the order
     * @param purchaseQty Self-explanatory
     * @param programId Galactic Marketplace program ID
     * @param stakingProgramId (optional) Atlas Staking program ID, used to find seller's ATLAS staking account for market fee reductions - defaults to mainnet staking program ID
     * @param registeredStake (optional) A `RegisteredStake` account, used to validate the seller's ATLAS staking account - defaults to the Atlas locker deployed by Star Atlas on mainnet
     */
    getCreateExchangeTransaction(connection, order, orderTaker, purchaseQty, programId, stakingProgramId = new web3_js_1.PublicKey('ATLocKpzDbTokxgvnLew3d7drZkEzLzDpzwgrgWKDbmc'), registeredStake = new web3_js_1.PublicKey('J5GhV1WKcEU98c1kZt36ixjaErrrPNWbZhg3JQDg114E')) {
        return __awaiter(this, void 0, void 0, function* () {
            const orderAccount = new web3_js_1.PublicKey(order.id);
            const assetMint = new web3_js_1.PublicKey(order.orderMint);
            const currencyMint = new web3_js_1.PublicKey(order.currencyMint);
            const orderInitializer = new web3_js_1.PublicKey(order.owner);
            const seller = order.orderType === Order_1.OrderSide.Buy
                ? orderTaker
                : new web3_js_1.PublicKey(order.owner);
            const orderTakerDepositTokenAccount = yield (0, util_1.getAssociatedTokenAddress)(orderTaker, order.orderType === Order_1.OrderSide.Buy ? assetMint : currencyMint);
            const currencyInfo = yield this.getRegisteredCurrencies(connection, programId);
            const { saVault } = currencyInfo.find((curr) => curr.mint === order.currencyMint);
            const [stakingAccount] = yield (0, atlas_staking_1.getStakingAccount)(stakingProgramId, seller, registeredStake);
            const { instructions, signers } = yield (0, instruction_builders_1.createExchangeInstruction)({
                connection,
                orderAccount,
                purchaseQty,
                orderTaker,
                orderTakerDepositTokenAccount,
                programId,
                expectedPrice: order.price,
                orderType: order.orderType,
                assetMint,
                currencyMint,
                orderInitializer,
                saVault: new web3_js_1.PublicKey(saVault),
                stakingProgramId,
                registeredStake,
                stakingAccount,
            });
            const transaction = (0, helpers_1.createTransactionFromInstructions)(instructions);
            return { transaction, signers };
        });
    }
    accountItemsToOrders(connection, programId, orderAccountItems, slotContext) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!orderAccountItems.length)
                return [];
            const currencyInfo = yield this.getRegisteredCurrencies(connection, programId);
            return orderAccountItems.map((accountItem) => {
                const id = accountItem.publicKey.toString();
                const orderType = Object.keys(accountItem.account.orderSide)[0] === 'buy'
                    ? Order_1.OrderSide.Buy
                    : Order_1.OrderSide.Sell;
                const orderMint = accountItem.account.assetMint.toString();
                const currencyMint = accountItem.account.currencyMint.toString();
                const price = accountItem.account.price;
                const orderQtyRemaining = accountItem.account.orderRemainingQty.toNumber();
                const orderOriginationQty = accountItem.account.orderOriginationQty.toNumber();
                const owner = accountItem.account.orderInitializerPubkey.toString();
                const ownerAssetTokenAccount = accountItem.account.initializerAssetTokenAccount.toString();
                const ownerCurrencyTokenAccount = accountItem.account.initializerCurrencyTokenAccount.toString();
                const createdAt = accountItem.account.createdAtTimestamp.toNumber();
                const { decimals: currencyDecimals } = currencyInfo.find((info) => info.mint.toString() === currencyMint);
                return new Order_1.Order({
                    id,
                    orderType,
                    orderMint,
                    currencyMint,
                    currencyDecimals,
                    price,
                    orderQtyRemaining,
                    orderOriginationQty,
                    owner,
                    ownerAssetTokenAccount,
                    ownerCurrencyTokenAccount,
                    createdAt,
                    slotContext,
                });
            });
        });
    }
}
exports.GmClientService = GmClientService;
//# sourceMappingURL=GmClientService.js.map